"use client";

import * as React from "react";
import { X } from "lucide-react";
import { cn } from "@modernatc/ui/cn";

interface Shortcut {
  keys: string[];
  description: string;
  scope?: string;
}

const SHORTCUTS: Shortcut[] = [
  // Study session
  { scope: "Study session", keys: ["1", "2", "3", "4"], description: "Pick an answer" },
  { scope: "Study session", keys: ["1"], description: "Rate Again (after answering)" },
  { scope: "Study session", keys: ["2"], description: "Rate Hard" },
  { scope: "Study session", keys: ["3"], description: "Rate Good" },
  { scope: "Study session", keys: ["4"], description: "Rate Easy" },
  { scope: "Study session", keys: ["←"], description: "Previous card" },
  { scope: "Study session", keys: ["→"], description: "Skip / Next card" },

  // Global
  { scope: "Global", keys: ["?"], description: "Show this help" },
  { scope: "Global", keys: ["Esc"], description: "Close dialogs" },
];

/**
 * Press `?` from anywhere to open a modal listing every keyboard
 * shortcut. Shortcuts are grouped by scope. Closes on Escape, click
 * outside the panel, or the close button.
 *
 * Standard SaaS pattern (Linear, GitHub, Notion). Discoverability is
 * the whole point — students who don't know about the rating hotkeys
 * never use them.
 */
export function KeyboardHelp() {
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    function onKey(e: KeyboardEvent) {
      // Don't fire `?` while the user is typing into an input.
      const target = e.target as HTMLElement | null;
      const isTyping =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target?.isContentEditable === true;
      if (e.key === "?" && !isTyping) {
        e.preventDefault();
        setOpen((v) => !v);
        return;
      }
      if (e.key === "Escape" && open) {
        e.preventDefault();
        setOpen(false);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!open) return null;

  // Group shortcuts by scope for the rendered list.
  const scopes = new Map<string, Shortcut[]>();
  for (const s of SHORTCUTS) {
    const k = s.scope ?? "General";
    const arr = scopes.get(k) ?? [];
    arr.push(s);
    scopes.set(k, arr);
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="keyboard-help-title"
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-[var(--color-panel-deep)]/70 px-4 py-12 backdrop-blur-sm"
      onClick={() => setOpen(false)}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={cn(
          "w-full max-w-lg overflow-hidden rounded-[var(--radius-md)] border",
          "border-[var(--color-panel-edge)] bg-[var(--color-panel)]",
          "shadow-[var(--shadow-panel)]",
        )}
      >
        <div className="flex items-center justify-between border-b border-[var(--color-panel-edge)] px-5 py-3">
          <div>
            <p className="text-callsign text-[10px] text-[var(--color-amber)]">
              ▸ Reference
            </p>
            <h2
              id="keyboard-help-title"
              className="mt-0.5 text-base font-semibold text-[var(--color-ink)]"
            >
              Keyboard shortcuts
            </h2>
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Close"
            className="-mr-1 flex size-8 items-center justify-center rounded-[var(--radius-sm)] text-[var(--color-ink-dim)] hover:bg-[var(--color-panel-raised)] hover:text-[var(--color-ink)]"
          >
            <X className="size-4" />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto p-5">
          <div className="flex flex-col gap-5">
            {Array.from(scopes.entries()).map(([scope, list]) => (
              <section key={scope}>
                <h3 className="text-callsign text-[10px] text-[var(--color-ink-muted)]">
                  {scope}
                </h3>
                <dl className="mt-2 flex flex-col gap-1.5">
                  {list.map((s, i) => (
                    <div
                      key={`${scope}-${i}`}
                      className="flex items-center justify-between gap-3 rounded-[var(--radius-sm)] px-2 py-1.5 hover:bg-[var(--color-panel-raised)]/40"
                    >
                      <dt className="text-sm text-[var(--color-ink-dim)]">
                        {s.description}
                      </dt>
                      <dd className="flex items-center gap-1">
                        {s.keys.map((k) => (
                          <kbd
                            key={k}
                            className="text-mono inline-flex min-w-6 items-center justify-center rounded-[var(--radius-sm)] border border-[var(--color-panel-edge)] bg-[var(--color-panel-deep)] px-1.5 py-0.5 text-[11px] text-[var(--color-ink)]"
                          >
                            {k}
                          </kbd>
                        ))}
                      </dd>
                    </div>
                  ))}
                </dl>
              </section>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between border-t border-[var(--color-panel-edge)] bg-[var(--color-panel-deep)]/40 px-5 py-3 text-callsign text-[10px] text-[var(--color-ink-muted)]">
          <span>Press <kbd className="text-mono text-[10px]">?</kbd> from anywhere</span>
          <span>Esc to close</span>
        </div>
      </div>
    </div>
  );
}
